import { Component, EventEmitter, Input, Output, SimpleChanges, OnChanges, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_DATE_FORMATS } from '@angular/material/core';

/** Display and parse format: MM/DD/YYYY. Payload is still emitted as YYYY-MM-DD for API. */
export const SIMPLE_DATE_FORMATS = {
  parse: { dateInput: 'MM/DD/YYYY' },
  display: {
    dateInput: 'MM/DD/YYYY',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'simple-date-picker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
  ],
  providers: [
    provideNativeDateAdapter(),
    { provide: MAT_DATE_FORMATS, useValue: SIMPLE_DATE_FORMATS },
  ],
  templateUrl: './simple-date-picker.component.html',
  styleUrl: './simple-date-picker.component.scss',
})
export class SimpleDatePickerComponent implements OnChanges, OnInit {
  /** Single date value: YYYY-MM-DD string, or first element if array (e.g. from filter). */
  @Input() valueFromParent: string | string[] | null = null;
  @Output() onDateSelect = new EventEmitter<string>();

  /** Internal model for mat-datepicker (Date or null). */
  value: Date | null = null;

  ngOnInit(): void {
    this.setValueFromParent(this.valueFromParent);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['valueFromParent']) {
      this.setValueFromParent(this.valueFromParent);
    }
  }

  private setValueFromParent(val: string | string[] | null): void {
    const str = val == null ? null : Array.isArray(val) ? (val[0] ?? '') : val;
    if (str == null || str === '') {
      this.value = null;
      return;
    }
    const parsed = this.parseToDate(str);
    this.value = parsed;
  }

  /** Accept YYYY-MM-DD or MM/DD/YYYY from parent. */
  private parseToDate(val: string): Date | null {
    if (!val || typeof val !== 'string') return null;
    const trimmed = val.trim();
    if (!trimmed) return null;
    // YYYY-MM-DD
    const iso = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
      const d = new Date(+iso[1], +iso[2] - 1, +iso[3]);
      return isNaN(d.getTime()) ? null : d;
    }
    // MM/DD/YYYY
    const us = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (us) {
      const d = new Date(+us[3], +us[1] - 1, +us[2]);
      return isNaN(d.getTime()) ? null : d;
    }
    const any = new Date(trimmed);
    return isNaN(any.getTime()) ? null : any;
  }

  onDateChange(date: Date | null): void {
    this.value = date;
    const payload = date ? this.toYYYYMMDD(date) : '';
    this.onDateSelect.emit(payload);
  }

  /** Emit YYYY-MM-DD for API (same as current filter payload). */
  private toYYYYMMDD(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}
